import { useState, useEffect, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createPortal } from 'react-dom';
import { api } from '../../lib/api.js';
import { toast } from '../../lib/toast.js';
import { SearchableSelect } from '../primitives/SearchableSelect.js';

interface Customer { id: string; name: string; }
interface Project { id: string; code: string; name: string; }

interface Props {
  onClose: () => void;
  initial?: {
    id?: string;
    name?: string;
    contactType?: string | null;
    customerId?: string | null;
    role?: string | null;
    dept?: string | null;
    email?: string | null;
    phone?: string | null;
    linkedin?: string | null;
    photo?: string | null;
    projects?: string[];
  };
}

function initials(name: string): string {
  return name.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase();
}
const AVC_PALETTE = ['#E8A427', '#22C55E', '#3B82F6', '#8B5CF6', '#EF4444', '#06B6D4', '#F97316', '#10B981', '#EC4899', '#14B8A6'];
function avc(name: string): string {
  return AVC_PALETTE[(name || '').charCodeAt(0) % AVC_PALETTE.length] ?? AVC_PALETTE[0]!;
}


export function AddContactModal({ onClose, initial }: Props) {
  const qc = useQueryClient();
  const editing = !!initial?.id;
  const [sigPaste, setSigPaste] = useState('');
  const [name, setName] = useState(initial?.name ?? '');
  const [contactType, setContactType] = useState(initial?.contactType ?? 'Customer');
  const [customerId, setCustomerId] = useState(initial?.customerId ?? '');
  const [role, setRole] = useState(initial?.role ?? '');
  const [dept, setDept] = useState(initial?.dept ?? '');
  const [email, setEmail] = useState(initial?.email ?? '');
  const [phone, setPhone] = useState(initial?.phone ?? '');
  const [linkedin, setLinkedin] = useState(initial?.linkedin ?? '');
  const [photo, setPhoto] = useState<string | null>(initial?.photo ?? null);
  const [photoUrl, setPhotoUrl] = useState('');
  const [photoErr, setPhotoErr] = useState('');
  const [photoDrag, setPhotoDrag] = useState(false);
  const photoFileRef = useRef<HTMLInputElement | null>(null);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [projSearch, setProjSearch] = useState('');
  const [selectedProjects, setSelectedProjects] = useState<string[]>(initial?.projects ?? []);

  useEffect(() => {
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') onClose(); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const { data: customers = [] } = useQuery({ queryKey: ['customers'], queryFn: () => api.get<Customer[]>('/customers').then((r) => r.data) });
  const { data: projects = [] } = useQuery({ queryKey: ['projects'], queryFn: () => api.get<Project[]>('/projects').then((r) => r.data) });

  const filteredProjects = projects.filter((p) => {
    const s = projSearch.toLowerCase();
    return !s || p.code.toLowerCase().includes(s) || p.name.toLowerCase().includes(s);
  });

  const toggleProject = (code: string) => {
    setSelectedProjects((prev) => prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]);
  };

  const applySig = () => {
    const text = sigPaste;
    if (!text.trim()) return;
    const em = text.match(/[\w.+-]+@[\w-]+\.[\w.-]+/);
    const ph = text.match(/(?:\+?44\s?|0)(?:\d\s?){9,10}/);
    const li = text.match(/linkedin\.com\/in\/[\w-]+/i);
    if (em && !email) setEmail(em[0]);
    if (ph && !phone) setPhone(ph[0].replace(/\s/g, ''));
    if (li && !linkedin) setLinkedin('https://www.' + li[0]);
    const firstLine = text.split('\n').map((l) => l.trim()).find((l) => l && /^[A-Za-z][A-Za-z .'-]+$/.test(l) && l.split(' ').length >= 2);
    if (firstLine && !name) setName(firstLine);
  };

  const onPhotoFile = async (file: File | null) => {
    setPhotoErr('');
    if (!file) return;
    if (!file.type.startsWith('image/')) { setPhotoErr('Not an image file'); return; }
    setPhotoUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const up = await api.post<{ url: string }>('/uploads/photo', fd);
      setPhoto(up.data.url);
    } catch {
      setPhotoErr('Photo upload failed');
    } finally {
      setPhotoUploading(false);
    }
  };
  const applyPhotoUrl = () => {
    setPhotoErr('');
    const v = photoUrl.trim();
    if (!v) return;
    if (!/^https?:\/\//i.test(v) && !v.startsWith('data:image/')) { setPhotoErr('Must be http(s) URL or data:image'); return; }
    setPhoto(v);
    setPhotoUrl('');
  };

  const save = useMutation({
    mutationFn: async () => {
      const body = {
        name: name.trim(),
        contactType,
        customerId: customerId || null,
        customer: customers.find((c) => c.id === customerId)?.name ?? null,
        role: role.trim() || null,
        dept: dept.trim() || null,
        email: email.trim() || null,
        phone: phone.trim() || null,
        linkedin: linkedin.trim() || null,
        photo,
        projects: selectedProjects,
      };
      if (editing && initial?.id) return api.patch(`/contacts/${initial.id}`, body);
      return api.post('/contacts', body);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contacts'] });
      toast.success(editing ? 'Contact updated' : 'Contact added', name);
      onClose();
    },
    onError: (e: any) => toast.error('Save failed', e?.response?.data?.message ?? 'Check fields and try again'),
  });

  return createPortal(
    <div className="mo open" onClick={onClose}>
      <div className="modal" style={{ width: 600 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-title" id="acontact-title">{editing ? '✏ Edit Contact' : '+ Add Contact'}</div>
        <details id="acontact-sig-details" style={{ marginBottom: 14, background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
          <summary style={{ cursor: 'pointer', padding: '9px 12px', fontSize: 11, color: 'var(--accent)', fontFamily: 'Syne, sans-serif', fontWeight: 700, letterSpacing: '.5px', textTransform: 'uppercase', listStyle: 'none', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span>📋 Paste email signature to auto-fill</span>
            <span style={{ fontSize: 10, color: 'var(--muted)', fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>click to expand</span>
          </summary>
          <div style={{ padding: '10px 12px', borderTop: '1px solid var(--border)' }}>
            <textarea className="form-textarea" id="acontact-sig-paste" placeholder="Paste the signature here…" style={{ minHeight: 90, fontSize: 11 }} value={sigPaste} onChange={(e) => setSigPaste(e.target.value)} onBlur={applySig} />
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 7, gap: 8 }}>
              <button type="button" className="btn btn-ghost btn-xs" onClick={() => setSigPaste('')}>🗑 Clear</button>
              <button type="button" className="btn btn-primary btn-xs" onClick={applySig}>Apply</button>
            </div>
          </div>
        </details>
        <div className="form-row">
          <label className="form-label">Photo <span style={{ fontSize: 10, color: 'var(--muted)', fontWeight: 400 }}>— optional, click / drop / paste URL</span></label>
          <div className="pp-wrap">
            <div
              className={`pp-preview${photo ? ' pp-has-photo' : ''}${photoDrag ? ' pp-drag' : ''}`}
              onClick={() => photoFileRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); setPhotoDrag(true); }}
              onDragLeave={() => setPhotoDrag(false)}
              onDrop={(e) => { e.preventDefault(); setPhotoDrag(false); const f = e.dataTransfer.files[0]; if (f) onPhotoFile(f); }}
              style={{ width: 88, height: 88 }}
            >
              {photo ? (
                <img src={photo} alt={name || 'photo'} />
              ) : name.trim() ? (
                <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: avc(name), color: '#fff', fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: 32 }}>{initials(name)}</div>
              ) : (
                <span className="pp-preview-hint">📷<br />Click or drop<br />a photo</span>
              )}
            </div>
            <div className="pp-controls">
              <div className="pp-btn-row">
                <button type="button" className="btn btn-ghost btn-xs" onClick={() => photoFileRef.current?.click()} disabled={photoUploading}>{photoUploading ? 'Uploading…' : '📁 Upload'}</button>
                <button type="button" className="btn btn-ghost btn-xs" onClick={() => { setPhoto(null); setPhotoErr(''); }}>🗑 Remove</button>
                <input
                  ref={photoFileRef}
                  type="file"
                  id="acontact-photo-file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) onPhotoFile(f); e.target.value = ''; }}
                />
              </div>
              <input
                type="text"
                className="pp-url"
                id="acontact-photo-url"
                placeholder="Or paste image URL and press Enter"
                value={photoUrl}
                onChange={(e) => setPhotoUrl(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); applyPhotoUrl(); } }}
              />
              <div className={`pp-err${photoErr ? ' show' : ''}`}>{photoErr}</div>
            </div>
          </div>
        </div>
        <div className="fg2">
          <div className="form-row"><label className="form-label">Full Name</label><input type="text" className="form-input" id="acontact-name" value={name} onChange={(e) => setName(e.target.value)} /></div>
          <div className="form-row">
            <label className="form-label">Contact Type</label>
            <SearchableSelect
              id="acontact-type"
              value={contactType ?? 'Other'}
              onChange={setContactType}
              placeholder="— Select type —"
              options={[
                { value: 'Customer', label: 'Customer' },
                { value: 'Worker', label: 'Worker' },
                { value: 'Supplier', label: 'Supplier' },
                { value: 'Contractor', label: 'Contractor' },
                { value: 'Consultant', label: 'Consultant' },
                { value: 'Internal', label: 'Internal' },
                { value: 'Other', label: 'Other' },
              ]}
            />
          </div>
          <div className="form-row">
            <label className="form-label">Company</label>
            <SearchableSelect
              id="acontact-customer"
              value={customerId ?? ''}
              onChange={setCustomerId}
              placeholder="— None —"
              options={[
                { value: '', label: '— None —' },
                ...customers.map((c) => ({ value: c.id, label: c.name })),
              ]}
            />
          </div>
        </div>
        <div className="fg2">
          <div className="form-row"><label className="form-label">Job Title</label><input type="text" className="form-input" id="acontact-role" value={role ?? ''} onChange={(e) => setRole(e.target.value)} /></div>
          <div className="form-row"><label className="form-label">Department</label><input type="text" className="form-input" id="acontact-dept" value={dept ?? ''} onChange={(e) => setDept(e.target.value)} /></div>
        </div>
        <div className="fg2">
          <div className="form-row"><label className="form-label">Email</label><input type="email" className="form-input" id="acontact-email" value={email ?? ''} onChange={(e) => setEmail(e.target.value)} /></div>
          <div className="form-row"><label className="form-label">Phone <span style={{ fontSize: 10, color: 'var(--muted)', fontWeight: 400 }}>— required, 11 digits</span></label><input type="tel" className="form-input" id="acontact-phone" value={phone ?? ''} onChange={(e) => setPhone(e.target.value)} /></div>
        </div>
        <div className="form-row"><label className="form-label">LinkedIn URL <span style={{ fontSize: 10, color: 'var(--muted)', fontWeight: 400 }}>— optional, leave blank to search by name</span></label><input type="url" className="form-input" id="acontact-linkedin" placeholder="https://www.linkedin.com/in/..." value={linkedin ?? ''} onChange={(e) => setLinkedin(e.target.value)} /></div>
        <div className="form-row">
          <label className="form-label">Linked Projects</label>
          <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 8, padding: 10 }}>
            <div style={{ display: 'flex', gap: 7, marginBottom: 8 }}>
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 7, padding: '5px 9px', gap: 6 }}>
                <span style={{ color: 'var(--faint)', fontSize: 13 }}>⌕</span>
                <input type="text" id="acontact-proj-search" placeholder="Search projects by ID or name…" value={projSearch} onChange={(e) => setProjSearch(e.target.value)} style={{ border: 'none', background: 'none', padding: 0, fontSize: 12, color: 'var(--text)', outline: 'none', flex: 1 }} />
              </div>
              <button className="btn btn-ghost btn-sm" onClick={() => setSelectedProjects([])}>Clear</button>
            </div>
            <div id="acontact-proj-list" style={{ maxHeight: 160, overflowY: 'auto', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4 }}>
              {filteredProjects.map((p) => {
                const checked = selectedProjects.includes(p.code);
                return (
                  <label
                    key={p.id}
                    style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 11, padding: '5px 7px', borderRadius: 6, border: `1px solid ${checked ? 'var(--accent)' : 'var(--border)'}`, background: 'var(--surface)', transition: 'border-color .1s' }}
                  >
                    <input
                      type="checkbox"
                      className="acontact-proj-cb"
                      checked={checked}
                      onChange={() => toggleProject(p.code)}
                      style={{ accentColor: 'var(--accent)' }}
                    />
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ fontWeight: 700, fontFamily: 'Syne, sans-serif', fontSize: 11, color: 'var(--accent)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.code}</div>
                      <div style={{ fontSize: 9, color: 'var(--muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</div>
                    </div>
                  </label>
                );
              })}
            </div>
          </div>
          <div id="acontact-proj-selected" style={{ marginTop: 7, display: 'flex', flexWrap: 'wrap', gap: 5, alignItems: 'center' }}>
            {selectedProjects.length === 0 ? (
              <span style={{ fontSize: 10, color: 'var(--faint)' }}>No projects linked yet</span>
            ) : (
              <>
                <span style={{ fontSize: 10, color: 'var(--muted)' }}>Linked:</span>
                {selectedProjects.map((code) => (
                  <span
                    key={code}
                    onClick={() => toggleProject(code)}
                    title="Click to remove"
                    style={{ background: 'var(--accent-dim)', border: '1px solid rgba(232,164,39,.3)', borderRadius: 5, padding: '2px 8px', fontSize: 10, color: 'var(--accent)', cursor: 'pointer' }}
                  >{code} ×</span>
                ))}
              </>
            )}
          </div>
        </div>
        <div className="modal-actions">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" id="acontact-save-btn" onClick={() => save.mutate()} disabled={save.isPending || photoUploading}>{save.isPending ? 'Saving…' : 'Save Contact'}</button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
